import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, ExternalLink } from "lucide-react";

/**
 * Iter54 · One-click Discord match thread button.
 * - If thread already exists → deep-link to Discord
 * - Else → create it (idempotent), then deep-link
 */
export default function MatchThreadButton({ fixture, onDone }) {
  const [busy, setBusy] = useState(false);
  const threadId = fixture.discord_thread_id;

  const open = async () => {
    if (threadId) {
      window.open(`https://discord.com/channels/@me/${threadId}`, "_blank", "noopener,noreferrer");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/fixtures/${fixture.id}/discord-thread`);
      toast.success(`Match thread ready · ${data.reps_pinged || 0} rep${data.reps_pinged === 1 ? "" : "s"} tagged`);
      if (data.thread_id) {
        window.open(`https://discord.com/channels/@me/${data.thread_id}`, "_blank", "noopener,noreferrer");
      }
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Match thread failed";
      // Iter55 · Longer toast when the message is a setup-guidance sentence
      toast.error(detail, { duration: detail.length > 60 ? 12000 : 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={open}
      disabled={busy}
      data-testid={`match-thread-${fixture.id}`}
      title={threadId ? "Open the Discord thread for this match" : "Create a Discord thread for this match & tag both reps"}
      className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 font-bold border transition-colors ${
        threadId
          ? "border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2] hover:text-white"
          : "border-[#5865F2] bg-[#5865F2]/10 text-[#5865F2] hover:bg-[#5865F2] hover:text-white"
      } disabled:opacity-50`}
    >
      <MessageSquare className="w-3 h-3" /> {threadId ? "Open thread" : busy ? "Opening…" : "Match thread"}
      {threadId && <ExternalLink className="w-3 h-3" />}
    </button>
  );
}
